import type { PriceQuote } from "@/lib/bookings-api";

/**
 * Climate-offset opt-in state needed to compose the checkout total. The flag
 * (`climateOffsetFlagOn`) gates the feature; `climateOffsetEnabled` is the
 * renter's actual toggle choice. Both must be true for the offset to be added.
 */
export interface CheckoutOffsetState {
  climateOffsetFlagOn: boolean;
  climateOffsetEnabled: boolean;
}

/**
 * Minimal slice of a PriceQuote needed to compose the renter-facing total.
 * Accepting a structural subset keeps this helper usable from both the client
 * quote (CheckoutPage) and the server-returned quote (booking result).
 */
export type CheckoutTotalQuote = Pick<
  PriceQuote,
  "paymentType" | "platformPaymentDue" | "paymentDue" | "climateOffsetAmount" | "tax"
>;

/**
 * Task #4263 — single source of truth for the renter-facing checkout "Total".
 *
 * This is BOTH the figure the Pay button displays (`totalDue` in CheckoutPage)
 * AND the figure the H3 price-drift guard compares the server quote against
 * (`serverTotal` in useCheckoutPaymentIntent). They MUST be composed identically
 * or any add-on layered on top of the base (climate offset, US sales tax, and
 * any future add-on) desyncs the two and trips a false "price has been updated"
 * prompt — even though the server priced the booking correctly.
 *
 * Composition (penny-rounded once at the end, matching the summary rows):
 *   base (partial-pay-aware) + climate offset (when opted in) + sales-tax estimate
 *
 * - base: `platformPaymentDue` in partial-pay markets (the card-only charge),
 *   otherwise `paymentDue` (the full booking value).
 * - climate offset: `climateOffsetAmount` only when the feature flag is on AND
 *   the renter opted in.
 * - sales tax: the estimated `tax.amount` only when `tax.applicable`. When the
 *   estimate is unknown (Stripe Tax outage / no pickup ZIP) `amount` is 0, so
 *   the total stays pre-tax — exactly as the display total does.
 */
export function composeCheckoutTotal(
  quote: CheckoutTotalQuote,
  offset: CheckoutOffsetState,
): number {
  const base =
    quote.paymentType === "partial" ? quote.platformPaymentDue : quote.paymentDue;
  const offsetAmount =
    offset.climateOffsetFlagOn && offset.climateOffsetEnabled
      ? quote.climateOffsetAmount || 0
      : 0;
  const taxAmount = quote.tax?.applicable ? quote.tax.amount || 0 : 0;
  return Math.round((base + offsetAmount + taxAmount) * 100) / 100;
}
